// @ts-check

import {
  isProxy,
  isReactive,
  isRef,
  toRaw,
} from 'vue';

/**
 * Recursively converts reactive objects to their raw values using Vue's toRaw function.
 *
 * @template {Record<string, any>} T
 * @param {T} sourceObj - The object to be converted.
 * @returns {T} - The object with all reactive parts replaced with their raw values.
 */
export function deepToRaw(sourceObj) {
  /**
   * @param {any} input - The input object to be converted.
   * @returns {any} The input with all reactive parts replaced with their raw values.
   */
  const objectIterator = (input) => {
    if (Array.isArray(input)) {
      return input.map((item) => objectIterator(item));
    }

    if (isRef(input) || isReactive(input) || isProxy(input)) {
      return objectIterator(toRaw(input));
    }

    if (input && typeof input === 'object') {
      return Object.keys(input).reduce(/** @param {T} acc */ (acc, key) => {
        acc[/** @type {keyof typeof acc} */ (key)] = objectIterator(input[key]);
        return acc;
      }, /** @type {T} */ ({}));
    }

    return input;
  };

  return objectIterator(sourceObj);
}
